import { IEventBusEmitData, IEventBusListenerData, IEventBusPlugin } from './core/types';

import { ConsoleLoggerPlugin } from './plugins/ConsoleLoggerPlugin';
import { EventBusStateManagerPlugin } from './plugins/EventBusStateManagerPlugin';

export interface IEventBusMonitorConfig {
  enableConsoleLogger?: boolean;
  enableHistoryTracker?: boolean;
  debug?: boolean;
}

const DEFAULT_CONFIG: IEventBusMonitorConfig = {
  enableConsoleLogger: false,
  enableHistoryTracker: true,
  debug: false
};

export class EventBusMonitor {
  private static instance: EventBusMonitor;
  private plugins: IEventBusPlugin[] = [];
  private sessionId: string;
  private startTime: number;

  private constructor(config: IEventBusMonitorConfig = DEFAULT_CONFIG) {
    this.sessionId = `eb-${Date.now()}`;
    this.startTime = Date.now();

    if (config.enableConsoleLogger !== false) {
      this.plugins.push(new ConsoleLoggerPlugin());
    }

    if (config.enableHistoryTracker !== false) {
      this.plugins.push(new EventBusStateManagerPlugin());
    }
  }

  public static getInstance(config?: IEventBusMonitorConfig): EventBusMonitor {
    if (!EventBusMonitor.instance) {
      EventBusMonitor.instance = new EventBusMonitor(config);
    }
    return EventBusMonitor.instance;
  }

  public registerPlugin(plugin: IEventBusPlugin): void {
    this.plugins.push(plugin);
  }

  public trackListener(data: IEventBusListenerData, isAttaching: boolean): void {
    this.plugins.forEach(plugin => {
      if (!plugin.enabled) return;

      if (isAttaching) {
        plugin.onListenerAttached?.(data);
      } else {
        plugin.onListenerDetached?.(data);
      }
    });
  }

  public trackEmit(data: IEventBusEmitData): void {
    this.plugins.forEach(plugin => {
      if (!plugin.enabled) return;
      plugin.onEventEmitted?.(data);
    });
  }

  public getPlugin<T extends IEventBusPlugin>(id: string): T | undefined {
    return this.plugins.find(plugin => plugin.id === id) as T | undefined;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public getUptime(): number {
    return Date.now() - this.startTime;
  }

  public getActiveListenersCount(): number {
    const stateManager = this.getPlugin<EventBusStateManagerPlugin>('event-bus-state-manager');
    return stateManager?.getActiveListenersCount() || 0;
  }
}

// Export a default instance for easy access
export const eventBusMonitor = EventBusMonitor.getInstance();
